/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import { useEffect, useMemo, useState } from 'react'
import { Container } from 'src/components/Layout'
import FullGameCard from './cards/FullGameCard'
import GameSummary from './cards/GameSummary'
import HighlightCard from './cards/HighlightCard'
import HighlightFilterPills from './filters/HighlightFilterPills'
import LiveScoreWidget from 'src/pages/Game/NewGamePageHeader/LiveScoreWidget'
import VideoPlayer from './player/VideoPlayer'
import {
  containerStyle,
  highlightsListStyle,
  leftPanelStyle,
  rightPanelStyle,
  sectionStyle,
  sectionTitleStyle,
} from './styles'
import { enrichHighlightsWithTeamSide } from './utils'

/**
 * HighlightsV2 component - Main container for the new highlights page
 * @param {Object} game - Game object containing game data
 * @returns {JSX.Element} HighlightsV2 component
 */
const HighlightsV2 = ({ game, highlightsData }) => {
  // State for selected video URL, selected item ID, active filter, full game status, and video offset
  const [selectedVideoUrl, setSelectedVideoUrl] = useState('')
  const [selectedItemId, setSelectedItemId] = useState('')
  const [activeFilter, setActiveFilter] = useState('All')
  const [isFullGameActive, setIsFullGameActive] = useState(true)
  const [videoOffsetSeconds, setVideoOffsetSeconds] = useState(0)

  // Enrich highlights with team side information
  const enrichedHighlights = useMemo(() => {
    return enrichHighlightsWithTeamSide(
      highlightsData,
      game?.homeTeamId,
      game?.visitingTeamId
    )
  }, [highlightsData, game?.homeTeamId, game?.visitingTeamId])

  // Filter highlights based on active filter
  const filteredHighlights = useMemo(() => {
    if (activeFilter === 'All') {
      return enrichedHighlights
    }

    // Filter by team side
    if (activeFilter === 'Home' || activeFilter === 'Visitor') {
      return enrichedHighlights.filter(
        (highlight) => highlight.teamSide === activeFilter
      )
    }

    // Filter by type
    return enrichedHighlights.filter(
      (highlight) => highlight.type === activeFilter
    )
  }, [activeFilter, enrichedHighlights])

  // Create full game object from game data
  const fullGame = useMemo(() => {
    if (!game?.broadcast_video_url) {
      return null
    }
    const firstHighlightThumb = enrichedHighlights?.[0]?.thumbnail_url
    return {
      id: 'full-game',
      videoUrl: game.broadcast_video_url,
      thumbnail:
        firstHighlightThumb ||
        '/images/generic_placeholders/placeholder_team.png',
      title: 'Full Game',
      duration: game.duration || 'Live',
    }
  }, [game, enrichedHighlights])

  // Create highlight categories dynamically
  const highlightCategories = useMemo(() => {
    const categories = ['All']

    // Add Home/Visitor if we have highlights
    if (enrichedHighlights.length > 0) {
      const hasHome = enrichedHighlights.some((h) => h.teamSide === 'Home')
      const hasVisitor = enrichedHighlights.some(
        (h) => h.teamSide === 'Visitor'
      )

      if (hasHome) {
        categories.push('Home')
      }
      if (hasVisitor) {
        categories.push('Visitor')
      }
    }

    return categories
  }, [enrichedHighlights])

  // Set default video selection
  useEffect(() => {
    if (game?.broadcast_video_url && !selectedVideoUrl) {
      setSelectedVideoUrl(game.broadcast_video_url)
      setSelectedItemId('full-game')
      setIsFullGameActive(true)
      // Set offset for full game if it exists
      if (game?.broadcast_video_offset_seconds) {
        console.log(
          'Setting default full game offset to:',
          game.broadcast_video_offset_seconds
        )
        setVideoOffsetSeconds(game.broadcast_video_offset_seconds)
      } else {
        setVideoOffsetSeconds(0)
      }
    } else if (
      !game?.broadcast_video_url &&
      enrichedHighlights.length > 0 &&
      !selectedVideoUrl
    ) {
      setSelectedVideoUrl(enrichedHighlights[0].video_url)
      setSelectedItemId(enrichedHighlights[0].id)
      setIsFullGameActive(false)
      setVideoOffsetSeconds(0)
    }
  }, [game?.broadcast_video_url, enrichedHighlights, selectedVideoUrl])

  // Handle card clicks
  const handleCardClick = (item) => {
    if (item.videoUrl) {
      setSelectedVideoUrl(item.videoUrl)
      setSelectedItemId(item.id)
      setIsFullGameActive(item.id === 'full-game')
      // Set offset for full game if it exists
      if (item.id === 'full-game' && game?.broadcast_video_offset_seconds) {
        console.log(
          'Setting full game offset to:',
          game.broadcast_video_offset_seconds
        )
        setVideoOffsetSeconds(game.broadcast_video_offset_seconds)
      } else {
        setVideoOffsetSeconds(0)
      }
    } else if (item.video_url) {
      setSelectedVideoUrl(item.video_url)
      setSelectedItemId(item.id)
      setIsFullGameActive(false)
      setVideoOffsetSeconds(0)
    }
  }

  // Handle filter changes
  const handleFilterChange = (filter) => {
    setActiveFilter(filter)
  }

  // Check if a card is currently playing
  const isCardPlaying = (item) => {
    return selectedItemId === item.id
  }

  return (
    <Container
      noWrap
      css={css`
        width: 100%;
        margin-bottom: 16px;
        @media (max-width: 768px) {
          margin-left: 0;
          margin-bottom: 60px;
        }
      `}
    >
      <div css={containerStyle}>
        {/* Left Panel - Video Player */}
        <div css={leftPanelStyle}>
          <div
            css={css`
              position: relative;
            `}
          >
            <VideoPlayer
              url={selectedVideoUrl}
              offsetSeconds={videoOffsetSeconds}
            />
            {isFullGameActive && (
              <LiveScoreWidget
                game={game}
                homeTeamName={game?.homeTeam?.fullName || game?.homeTeamName}
                visitorTeamName={
                  game?.visitingTeam?.fullName || game?.visitingTeamName
                }
              />
            )}
          </div>
          {/* <div css={progressPlaceholderStyle}>
            Video progress controls will be implemented here
          </div> */}
          <GameSummary
            summary={
              game?.game_summary
                ? {
                    title: game.game_summary.headline,
                    content: game.game_summary.summary,
                  }
                : null
            }
          />
        </div>

        {/* Right Panel - Cards and Highlights */}
        <div css={rightPanelStyle}>
          {/* Full Game Section */}
          {fullGame && (
            <div css={sectionStyle}>
              <div css={sectionTitleStyle}>Full Game</div>
              <FullGameCard
                game={fullGame}
                onClick={handleCardClick}
                isPlaying={isCardPlaying(fullGame)}
              />
            </div>
          )}

          {/* Highlights Section */}
          <div css={sectionStyle}>
            <div css={sectionTitleStyle}>Highlights</div>
            <HighlightFilterPills
              categories={highlightCategories}
              activeFilter={activeFilter}
              onFilterChange={handleFilterChange}
            />
            <div css={highlightsListStyle}>
              {filteredHighlights.length > 0 ? (
                filteredHighlights.map((highlight) => (
                  <HighlightCard
                    key={highlight.id}
                    highlight={highlight}
                    onClick={handleCardClick}
                    isPlaying={isCardPlaying(highlight)}
                  />
                ))
              ) : (
                <div
                  css={css`
                    padding: 24px;
                    text-align: center;
                    color: #666;
                  `}
                >
                  No highlights available yet
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Container>
  )
}

export default HighlightsV2
